import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Eye, EyeOff } from 'lucide-react'

import { cn } from '@/lib/utils'
import { useAuth } from '@/shared/auth'
import { getAuthErrorMessage } from '@/shared/lib/auth-errors'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { LanguageSwitchButton } from '@/shared/ui/language-switch-button'

interface LoginFormProps extends React.ComponentPropsWithoutRef<'div'> {
    onSwitchToSignup?: () => void
    onSwitchToPasswordRecovery?: () => void
    onSuccess?: () => void
}

export function LoginForm({
    className,
    onSwitchToSignup,
    onSwitchToPasswordRecovery,
    onSuccess,
    ...props
}: LoginFormProps) {
    const { t } = useTranslation()
    const { signInWithEmail } = useAuth()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [showPassword, setShowPassword] = useState(false)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setIsLoading(true)

        try {
            await signInWithEmail(email, password)
            onSuccess?.()
        } catch (err: unknown) {
            setError(getAuthErrorMessage(err, t, 'login'))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className={cn('flex flex-col gap-6', className)} {...props}>
            <div className='absolute right-4 top-4'>
                <LanguageSwitchButton />
            </div>
            <Card>
                <CardHeader>
                    <CardTitle className='text-2xl'>{t('auth.login.title', 'Iniciar Sesión')}</CardTitle>
                    <CardDescription>
                        {t('auth.login.description', 'Ingresa tu email y contraseña para acceder')}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit}>
                        <div className='flex flex-col gap-6'>
                            {error && <div className='rounded-md bg-red-50 p-3 text-sm text-red-800'>{error}</div>}
                            <div className='grid gap-2'>
                                <Label htmlFor='email'>{t('auth.login.email', 'Email')}</Label>
                                <Input
                                    id='email'
                                    type='email'
                                    placeholder={t('auth.login.emailPlaceholder', 'tu@email.com')}
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                            <div className='grid gap-2'>
                                <div className='flex items-center'>
                                    <Label htmlFor='password'>{t('auth.login.password', 'Contraseña')}</Label>
                                    <button
                                        type='button'
                                        onClick={onSwitchToPasswordRecovery}
                                        className='ml-auto inline-block text-sm underline-offset-4 hover:underline'
                                    >
                                        {t('auth.login.forgotPassword', '¿Olvidaste tu contraseña?')}
                                    </button>
                                </div>
                                <div className='relative'>
                                    <Input
                                        id='password'
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        required
                                        disabled={isLoading}
                                        className='pr-10'
                                    />
                                    <button
                                        type='button'
                                        onClick={() => setShowPassword(!showPassword)}
                                        className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none'
                                        tabIndex={-1}
                                    >
                                        {showPassword ? (
                                            <EyeOff className='h-4 w-4' />
                                        ) : (
                                            <Eye className='h-4 w-4' />
                                        )}
                                    </button>
                                </div>
                            </div>
                            <Button type='submit' className='w-full' disabled={isLoading}>
                                {isLoading
                                    ? t('auth.login.loading', 'Iniciando sesión...')
                                    : t('auth.login.submit', 'Iniciar Sesión')}
                            </Button>
                        </div>
                        <div className='mt-4 text-center text-sm'>
                            {t('auth.login.noAccount', '¿No tienes cuenta?')}{' '}
                            <button type='button' onClick={onSwitchToSignup} className='underline underline-offset-4'>
                                {t('auth.login.signupLink', 'Regístrate')}
                            </button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    )
}
